import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Stack, Switch, TextField, Typography,
} from '@mui/material';
import { useNotify } from 'react-admin';
import authInstance from '../../services/httpClient';
import { useAdminTheme } from '../../admin-theme';
import { stagingApiBase } from './stagingOrg';

// Edit dialog — correct a staged item's fields before approving it. Shared by
// Pending Review and Flagged Items. Saved to the server (PATCH) rather than held
// here, so a correction survives a reload and is applied by bulk approve too
// (which sends no per-item overrides).

/** The staged values the dialog edits, as the caller has them loaded. */
export interface StagedEditable {
  sku: string;
  /** A field-change-only row has no staged item: its Sell in app / 18+ belong to
   *  the product (Product Management), so the dialog does not offer them. */
  type?: 'new_item' | 'field_update' | 'removal' | null;
  name: string | null;
  description: string | null;
  basePrice: number | null;
  category: string | null;
  /** Comma-separated. */
  tags: string | null;
  brand: string | null;
  gtin: string | null;
  imageUrl: string | null;
  sellInApp: boolean;
  isRestricted: boolean;
  /** Detail responses carry this; list rows leave it undefined. */
  sellInAppOverridden?: boolean;
}

export interface StagedItemEditDialogProps {
  open: boolean;
  item: StagedEditable | null;
  onClose: () => void;
  /** Called after a successful save with the row as stored (the PATCH response's item). */
  onSaved: (savedItem?: any) => void;
}

interface Suggestions {
  brand: { value: string; reason: string } | null;
  category: { value: string; reason: string } | null;
  tags: { value: string[]; reason: string } | null;
  description: { value: string; reason: string } | null;
  basePrice: { value: number; reason: string } | null;
  gtin: { value: string; reason: string } | null;
}

interface EditForm {
  name: string;
  description: string;
  basePrice: string;
  category: string;
  tags: string;
  brand: string;
  gtin: string;
  imageUrl: string;
  sellInApp: boolean;
  isRestricted: boolean;
}

const formFrom = (item: StagedEditable): EditForm => ({
  name: item.name ?? '',
  description: item.description ?? '',
  basePrice: item.basePrice != null ? String(item.basePrice) : '',
  category: item.category ?? '',
  tags: item.tags ?? '',
  brand: item.brand ?? '',
  gtin: item.gtin ?? '',
  imageUrl: item.imageUrl ?? '',
  sellInApp: item.sellInApp !== false,
  isRestricted: item.isRestricted === true,
});

const EMPTY_FORM: EditForm = {
  name: '', description: '', basePrice: '', category: '', tags: '', brand: '', gtin: '', imageUrl: '',
  sellInApp: true, isRestricted: false,
};

export default function StagedItemEditDialog({ open, item: itemProp, onClose, onSaved }: StagedItemEditDialogProps) {
  // Keep showing the last item while the dialog fades out after the caller clears it.
  const lastItem = useRef<StagedEditable | null>(itemProp);
  if (itemProp) lastItem.current = itemProp;
  const item = itemProp ?? lastItem.current;
  const { colors } = useAdminTheme();
  const notify = useNotify();
  const [form, setForm] = useState<EditForm>(() => (item ? formFrom(item) : EMPTY_FORM));
  const [saving, setSaving] = useState(false);
  const [suggestions, setSuggestions] = useState<Suggestions | null>(null);
  const [previewFailed, setPreviewFailed] = useState(false);
  const sku = item?.sku ?? null;

  // Seed the form whenever a (new) item is opened. Suggestions are PER SKU: clear
  // first, then always refetch for this SKU — reusing the previous SKU's would show
  // its values and "Apply suggestions" would write them into this one.
  useEffect(() => {
    if (!open || !itemProp) return undefined;
    setForm(formFrom(itemProp));
    setSuggestions(null);
    let current = true;
    authInstance
      .get(`${stagingApiBase()}/staging/items/${itemProp.sku}/suggestions`)
      .then(({ data }) => { if (current) setSuggestions(data); })
      .catch(() => { /* suggestions are optional */ });
    return () => { current = false; };
  }, [open, itemProp]);

  useEffect(() => { setPreviewFailed(false); }, [form.imageUrl]);

  const close = () => { if (!saving) onClose(); };

  const handleSave = async () => {
    if (!item || !sku) return;
    setSaving(true);
    try {
      const trimmedTags = form.tags.split(',').map((t) => t.trim()).filter(Boolean);
      const imageUrl = form.imageUrl.trim();
      const initial = formFrom(item);
      const { data } = await authInstance.patch(`${stagingApiBase()}/staging/items/${sku}`, {
        name: form.name.trim(),
        description: form.description.trim() || null,
        basePrice: form.basePrice.trim() === '' ? null : Number(form.basePrice),
        // Blank is a valid, intended state for brand — cleared means cleared, it is
        // never backfilled with a placeholder.
        brand: form.brand.trim() || null,
        tags: trimmedTags.length > 0 ? trimmedTags : null,
        category: form.category.trim() || null,
        gtin: form.gtin.trim() || null,
        // Only what the reviewer changed: any sellInApp in the PATCH marks it
        // overridden and stops feed refreshes.
        ...(form.sellInApp !== initial.sellInApp ? { sellInApp: form.sellInApp } : {}),
        ...(form.isRestricted !== initial.isRestricted ? { isRestricted: form.isRestricted } : {}),
        ...(imageUrl !== initial.imageUrl.trim() ? { imageUrl: imageUrl || null } : {}),
      });
      notify(`${sku} updated`, { type: 'success' });
      onSaved(data?.item);
      onClose();
    } catch (err: any) {
      notify(`Failed to save: ${err.response?.data?.error || err.message}`, { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const initialFlags = item ? formFrom(item) : null;
  const imageUrl = form.imageUrl.trim();

  return (
    <Dialog
      open={open}
      onClose={close}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { bgcolor: colors.bgCard, border: `1px solid ${colors.border}`, backgroundImage: 'none' } }}
    >
      <DialogTitle sx={{ color: colors.textPrimary }}>
        Edit {sku}
        <Typography variant="caption" sx={{ display: 'block', color: colors.textMuted, fontWeight: 400 }}>
          Saved to the staged item. Approve afterwards to apply it to the catalogue.
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Name"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            fullWidth
            size="small"
          />
          <TextField
            label="Description"
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            fullWidth
            size="small"
            multiline
            minRows={2}
            helperText={suggestions?.description ? `Suggested: ${suggestions.description.reason}` : undefined}
          />
          <Stack direction="row" spacing={2}>
            <TextField
              label="Base Price"
              value={form.basePrice}
              onChange={(e) => setForm((f) => ({ ...f, basePrice: e.target.value }))}
              fullWidth
              size="small"
              type="number"
              inputProps={{ min: 0, step: '0.01' }}
              helperText={
                suggestions?.basePrice
                  ? `Suggested: R${suggestions.basePrice.value.toFixed(2)} — ${suggestions.basePrice.reason}`
                  : undefined
              }
            />
            <TextField
              label="Barcode"
              value={form.gtin}
              onChange={(e) => setForm((f) => ({ ...f, gtin: e.target.value }))}
              fullWidth
              size="small"
              helperText={
                suggestions?.gtin ? `Suggested: ${suggestions.gtin.value} — ${suggestions.gtin.reason}` : undefined
              }
            />
          </Stack>
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <TextField
              label="Image URL"
              value={form.imageUrl}
              onChange={(e) => setForm((f) => ({ ...f, imageUrl: e.target.value }))}
              fullWidth
              size="small"
              helperText="An http(s) link to the product image, imported when approved. Leave blank for none."
            />
            {imageUrl && !previewFailed && (
              <Box
                component="img"
                src={imageUrl}
                alt="Image preview"
                onError={() => setPreviewFailed(true)}
                sx={{
                  width: 56, height: 56, objectFit: 'contain', flexShrink: 0,
                  borderRadius: 1, border: `1px solid ${colors.border}`, bgcolor: colors.bgElevated,
                }}
              />
            )}
          </Stack>
          <TextField
            label="Category"
            value={form.category}
            onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
            fullWidth
            size="small"
            helperText={
              suggestions?.category
                ? `Suggested: ${suggestions.category.value} — ${suggestions.category.reason}`
                : 'The category this product will be created under.'
            }
          />
          <TextField
            label="Brand"
            value={form.brand}
            onChange={(e) => setForm((f) => ({ ...f, brand: e.target.value }))}
            fullWidth
            size="small"
            helperText={
              suggestions?.brand
                ? `Suggested: ${suggestions.brand.value} — ${suggestions.brand.reason}`
                : 'Leave blank if the product has no brand — blank is valid.'
            }
          />
          <TextField
            label="Tags"
            value={form.tags}
            onChange={(e) => setForm((f) => ({ ...f, tags: e.target.value }))}
            fullWidth
            size="small"
            helperText={
              suggestions?.tags
                ? `Suggested: ${suggestions.tags.value.join(', ')} — ${suggestions.tags.reason}`
                : 'Comma-separated.'
            }
          />
          {item?.type !== 'field_update' && initialFlags && (
            <>
              <Stack direction="row" spacing={3}>
                <FormControlLabel
                  control={(
                    <Switch
                      checked={form.sellInApp}
                      onChange={(e) => setForm((f) => ({ ...f, sellInApp: e.target.checked }))}
                    />
                  )}
                  label="Sell in app"
                  sx={{ color: colors.textPrimary }}
                />
                <FormControlLabel
                  control={(
                    <Switch
                      checked={form.isRestricted}
                      onChange={(e) => setForm((f) => ({ ...f, isRestricted: e.target.checked }))}
                    />
                  )}
                  label="18+"
                  sx={{ color: colors.textPrimary }}
                />
              </Stack>
              <Typography variant="caption" sx={{ color: colors.textMuted, mt: '4px !important' }}>
                {form.sellInApp !== initialFlags.sellInApp
                  ? 'Changing sell in app keeps your choice: later Vend syncs will not reset it.'
                  : item?.sellInAppOverridden
                    ? 'Sell in app was set by a reviewer: Vend syncs no longer change it.'
                    : 'Sell in app follows the woocommerce tag in Vend until you change it here.'}
              </Typography>
            </>
          )}
          {/* One-click accept for whatever was suggested but is still blank. Explicit,
              never automatic — a suggestion is evidence, not a decision. */}
          {(suggestions?.category || suggestions?.brand || suggestions?.tags
            || suggestions?.description || suggestions?.basePrice || suggestions?.gtin) && (
            <Button
              size="small"
              onClick={() => setForm((f) => ({
                ...f,
                category: f.category || suggestions?.category?.value || '',
                brand: f.brand || suggestions?.brand?.value || '',
                tags: f.tags || suggestions?.tags?.value.join(', ') || '',
                description: f.description || suggestions?.description?.value || '',
                basePrice: f.basePrice || (suggestions?.basePrice ? String(suggestions.basePrice.value) : ''),
                gtin: f.gtin || suggestions?.gtin?.value || '',
              }))}
              sx={{ alignSelf: 'flex-start', color: colors.info }}
            >
              Apply suggestions to empty fields
            </Button>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={saving} sx={{ color: colors.textSecondary }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || !form.name.trim()}
          sx={{ bgcolor: colors.info, color: colors.bgDeepest, fontWeight: 700 }}
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
